import React, { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Compass, Tent, Sparkles, Sun, Facebook, Instagram, Rocket, Heart,
  CloudRain, MapPin, Dog, Zap, Crown, Star, Users, MessageSquare, Camera,
} from "lucide-react";
import { api } from "../lib/api";
import { SOCIAL } from "../lib/social";

/**
 * /share/launch-day — THE launch-day reel.
 *
 * Five 9:16 scenes designed to be screen-recorded one by one (or all five
 * stitched as a carousel). Designed to load with maximum visual impact and
 * the hook "WE ARE READY. ARE YOU?" — the rest pulls visitors through with
 * the numbers, the features, the 14-day free Pro trial, and finally the QR
 * + follow buttons.
 *
 *   Scene 1 — WE ARE READY. ARE YOU?         (the hook)
 *   Scene 2 — THE NUMBERS                    (22,000+ sites · 14 days free)
 *   Scene 3 — WHAT YOU GET                   (six feature pills)
 *   Scene 4 — 14-DAY FREE PRO TRIAL          (the lure + QR)
 *   Scene 5 — FOLLOW / TAG / SHARE           (community CTA)
 *
 * Caption pack underneath the reel for one-tap copy/paste.
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const SIGNUP_URL = `${PUBLIC_URL}/register?ref=launch-day-reel`;

// Bright, scroll-stopping outdoor photo — same camping pool the rest of
// the app uses, golden-hour vibe.
const HERO_PHOTO_1 =
  "https://images.pexels.com/photos/1687845/pexels-photo-1687845.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop";
const HERO_PHOTO_2 =
  "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=1080&h=1920&fit=crop&auto=format&q=80";
const HERO_PHOTO_3 =
  "https://images.unsplash.com/photo-1455587734955-081b22074882?w=1080&h=1920&fit=crop&auto=format&q=80";

function Sticker({ children, color = "#ff4500", textColor = "#ffffff" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em]"
      style={{ background: color, color: textColor }}
    >
      {children}
    </span>
  );
}

function Scene({ children, bg, testid, label }) {
  return (
    <div className="flex flex-col gap-2 items-center">
      <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500]">{label}</div>
      <section
        className="relative w-full mx-auto flex flex-col justify-between text-white overflow-hidden"
        style={{
          aspectRatio: "9 / 16",
          maxWidth: 480,
          backgroundImage: bg,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid={testid}
      >
        {children}
        <div
          className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/65 backdrop-blur-sm text-white text-[10px] sm:text-[11px] font-bold mono uppercase tracking-[0.22em] text-center pointer-events-none z-10"
          aria-hidden="true"
        >
          🌐 camp-compass.uk
        </div>
      </section>
    </div>
  );
}

export default function LaunchDayReel() {
  const [count, setCount] = useState(22000);

  useEffect(() => {
    let alive = true;
    try {
      api.get("/campsites/stats")
        .then(({ data }) => { if (alive && data?.total) setCount(data.total); })
        .catch(() => {});
    } catch { /* network — fall back to default */ }
    return () => { alive = false; };
  }, []);

  const fmt = useMemo(() => count.toLocaleString("en-GB"), [count]);

  // ---------- caption pack ----------
  const captions = useMemo(() => [
    {
      title: "Caption · WE ARE READY (signature post)",
      body: `🚀 WE ARE READY. ARE YOU?

CampCompass UK is LIVE today — the biggest UK campsite map ever built.

✓ ${fmt} UK campsites · ONE map · ONE tap
✓ Filter by dogs · showers · electric · price · pitch type
✓ 48-hour weather alerts before every trip
✓ Save your shortlist · book in one tap
✓ Trip Wall — real camper photos from this week
✓ Campfire community — already buzzing

🎁 14-day FREE Pro trial for everyone signing up today. No queue. No catch.

👉 camp-compass.uk

Tag every camper you know — let's fill the map together 🏴󠁧󠁢󠁥󠁮󠁧󠁿⛺

#ukcamping #campsiteuk #ukcamping2026 #campingweekend #launchday #ukstaycation`,
    },
    {
      title: "Caption · Short hook (Instagram / TikTok)",
      body: `🚀 WE ARE READY. ARE YOU?

${fmt} UK campsites. One app. LIVE today.

🎁 14 days of Pro FREE for everyone joining today.

👉 camp-compass.uk

#ukcamping #launchday #campsiteuk #campingweekend`,
    },
    {
      title: "Caption · Bank holiday angle",
      body: `Bank holiday weekend, decision paralysis, twelve browser tabs open. 😩

We fixed it.

${fmt} UK campsites — ONE map. Compare prices, dogs, showers, EHU in seconds. Book direct in one tap.

🚀 LIVE NOW. Sign up today → 14 days of Pro FREE.

👉 camp-compass.uk

#bankholiday #ukcamping #campervan #ukstaycation #campingweekend #launchday`,
    },
    {
      title: "Caption · WhatsApp / personal share",
      body: `Hey! 🚀 CampCompass UK launched today.

It's the biggest UK campsite map ever built (${fmt}+ sites) — free, plus a 14-day Pro trial for everyone joining today.

Sign up in 20 secs: camp-compass.uk

If you can share with one camping mate, I'd be over the moon 🏕

— Anthony`,
    },
  ], [fmt]);

  return (
    <div className="bg-[#0e0e0e] min-h-screen text-white" data-testid="launch-day-reel-page">
      <header className="max-w-3xl mx-auto px-5 py-8">
        <div className="eyebrow text-[#ff4500] mb-2 flex items-center gap-2">
          <Rocket className="w-3.5 h-3.5" /> Launch day reel · 31 May 2026
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mb-3">
          We are ready.<br />Are you?
        </h1>
        <p className="text-sm text-[#bbb] leading-relaxed max-w-xl">
          Five 9:16 scenes built for screen-recording. Hold each scene for 2-3 seconds, stitch in the Reels / TikTok editor,
          add a punchy sound, paste a caption. Goal: convert every scroll-stopper into a signup TODAY.
        </p>
      </header>

      {/* ============ REEL SCENES ============ */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 px-3 pb-10 max-w-7xl mx-auto justify-items-center">

        {/* ── SCENE 1: THE HOOK ── */}
        <Scene
          label="Scene 1 · hook"
          testid="scene-1-hook"
          bg={`linear-gradient(180deg, rgba(255,69,0,0.35) 0%, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.85) 100%), url("${HERO_PHOTO_1}")`}
        >
          <div className="p-6 pt-10 flex justify-between gap-2">
            <Sticker color="#ffffff" textColor="#0e0e0e">
              <Rocket className="w-3.5 h-3.5" /> LIVE NOW
            </Sticker>
            <Sticker color="#ff4500">
              <Sun className="w-3.5 h-3.5" /> 31 MAY 2026
            </Sticker>
          </div>
          <div className="px-6 text-center">
            <div className="font-display text-[64px] sm:text-[84px] leading-[0.92] mb-3 drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)]">
              WE ARE<br />READY.
            </div>
            <div
              className="inline-block font-display text-[44px] sm:text-[60px] px-4 py-1 leading-tight"
              style={{ background: "#ff4500", color: "#fff" }}
            >
              ARE YOU?
            </div>
          </div>
          <div className="p-6 pb-8 text-center">
            <div className="text-sm sm:text-base mono uppercase tracking-[0.25em] text-white/95">
              The biggest UK campsite map.
            </div>
            <div className="text-xs sm:text-sm mono uppercase tracking-[0.25em] text-[#ffe7d6] mt-1">
              Officially open · keep scrolling 👇
            </div>
          </div>
        </Scene>

        {/* ── SCENE 2: THE NUMBERS ── */}
        <Scene
          label="Scene 2 · the numbers"
          testid="scene-2-numbers"
          bg={`linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(255,69,0,0.35) 50%, rgba(0,0,0,0.85) 100%), url("${HERO_PHOTO_2}")`}
        >
          <div className="p-6 pt-10">
            <Sticker color="#ffffff" textColor="#0e0e0e">
              <Sparkles className="w-3.5 h-3.5" /> By the numbers
            </Sticker>
          </div>
          <div className="px-6 text-center space-y-5">
            <div>
              <div className="font-display text-[80px] sm:text-[110px] leading-none drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)]" data-testid="scene-2-count">
                {fmt}
              </div>
              <div className="text-base sm:text-lg mono uppercase tracking-[0.25em] text-[#ffe7d6] -mt-1">
                UK campsites
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 text-3xl sm:text-4xl opacity-90">·</div>
            <div>
              <div className="font-display text-[52px] sm:text-[72px] leading-none">
                14 DAYS
              </div>
              <div className="text-sm sm:text-base mono uppercase tracking-[0.25em] text-[#ffe7d6] mt-1">
                of Pro · FREE today
              </div>
            </div>
          </div>
          <div className="p-6 pb-8 text-center">
            <div className="text-xs sm:text-sm mono uppercase tracking-[0.25em] text-white/85">
              No queue · no catch · cancel anytime
            </div>
          </div>
        </Scene>

        {/* ── SCENE 3: WHAT YOU GET ── */}
        <Scene
          label="Scene 3 · what you get"
          testid="scene-3-features"
          bg="linear-gradient(180deg, #ff4500 0%, #b73200 100%)"
        >
          <div className="p-6 pt-10">
            <Sticker color="#ffffff" textColor="#ff4500">
              <Heart className="w-3.5 h-3.5" /> What you get
            </Sticker>
          </div>
          <div className="px-6">
            <div className="font-display text-[36px] sm:text-[44px] leading-[0.95] mb-5 text-center">
              Everything in one app.
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <Feature icon={<MapPin className="w-5 h-5" />} label={`${fmt} sites`} />
              <Feature icon={<Dog className="w-5 h-5" />} label="Dog filter" />
              <Feature icon={<Zap className="w-5 h-5" />} label="EHU filter" />
              <Feature icon={<CloudRain className="w-5 h-5" />} label="Weather alerts" />
              <Feature icon={<Camera className="w-5 h-5" />} label="Trip Wall" />
              <Feature icon={<MessageSquare className="w-5 h-5" />} label="Campfire chat" />
              <Feature icon={<Sparkles className="w-5 h-5" />} label="AI packing" />
              <Feature icon={<Star className="w-5 h-5" />} label="Save & compare" />
            </div>
          </div>
          <div className="p-6 pb-8 text-center">
            <div className="text-xs mono uppercase tracking-[0.25em] text-white/95">
              All free · Pro perks for 14 days
            </div>
          </div>
        </Scene>

        {/* ── SCENE 4: TRIAL + QR ── */}
        <Scene
          label="Scene 4 · 14-day Pro trial"
          testid="scene-4-trial"
          bg={`linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.85) 100%), url("${HERO_PHOTO_3}")`}
        >
          <div className="p-6 pt-10 flex justify-between gap-2">
            <Sticker color="#ffffff" textColor="#0e0e0e">
              <Crown className="w-3.5 h-3.5" /> 14-day FREE
            </Sticker>
            <Sticker color="#ff4500">
              <Rocket className="w-3.5 h-3.5" /> LIVE NOW
            </Sticker>
          </div>
          <div className="px-6 text-center">
            <div className="font-display text-[48px] sm:text-[64px] leading-[0.95] drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)]">
              SIGN UP<br />FREE TODAY
            </div>
            <div
              className="inline-block font-display text-xl sm:text-2xl px-3 py-1 mt-3"
              style={{ background: "#ff4500", color: "#fff" }}
            >
              14 days of Pro on us
            </div>
          </div>
          <div
            className="p-5 sm:p-6 space-y-3"
            style={{
              background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.88) 100%)",
            }}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="text-left min-w-0">
                <div className="text-[10px] mono uppercase tracking-[0.25em] text-white/90 mb-1">
                  Scan or visit
                </div>
                <div className="font-display text-base sm:text-lg leading-tight break-all">
                  camp-compass.uk
                </div>
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-[#ffe7d6] mt-1">
                  20-second signup
                </div>
              </div>
              <div className="bg-white p-1.5 shrink-0" data-testid="launch-day-qr">
                <QRCodeSVG value={SIGNUP_URL} size={104} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
              </div>
            </div>
          </div>
        </Scene>

        {/* ── SCENE 5: FOLLOW + TAG ── */}
        <Scene
          label="Scene 5 · follow & tag"
          testid="scene-5-follow"
          bg="linear-gradient(180deg, #0e0e0e 0%, #1a1a1a 50%, #0e0e0e 100%)"
        >
          <div className="p-6 pt-10">
            <Sticker color="#ff4500">
              <Users className="w-3.5 h-3.5" /> Join the campfire
            </Sticker>
          </div>
          <div className="px-6 text-center">
            <div className="font-display text-[44px] sm:text-[56px] leading-[0.95] mb-3">
              TAG YOUR<br />CAMPING<br />MATES 👇
            </div>
            <div className="text-sm sm:text-base text-[#ffe7d6] mt-3 mono uppercase tracking-[0.18em]">
              Let's fill the map together
            </div>
          </div>
          <div
            className="p-5 sm:p-6 space-y-3"
            style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, #000 100%)" }}
          >
            <div className="text-center text-[10px] mono uppercase tracking-[0.25em] text-white/85 mb-2">
              Follow for daily campsite drops · honest UK camping tips
            </div>
            <div className="flex items-center justify-center gap-2">
              <a
                href={SOCIAL.facebook.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="scene-5-fb"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-white text-xs font-bold mono uppercase tracking-[0.14em]"
                style={{ background: "#1877f2" }}
              >
                <Facebook className="w-4 h-4" /> Facebook
              </a>
              <a
                href={SOCIAL.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="scene-5-ig"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-white text-xs font-bold mono uppercase tracking-[0.14em]"
                style={{ background: "linear-gradient(135deg,#fa7e1e 0%,#d62976 50%,#962fbf 100%)" }}
              >
                <Instagram className="w-4 h-4" /> Instagram
              </a>
            </div>
            <div className="text-center text-[10px] mono uppercase tracking-[0.25em] text-white/70 pt-1">
              camp-compass.uk
            </div>
          </div>
        </Scene>
      </div>

      <div className="text-center text-xs text-[#888] mono uppercase tracking-[0.2em] max-w-md mx-auto px-5 pb-8">
        Screen-record each scene on your phone (1080×1920) or in a 540×960 desktop window.
      </div>

      {/* ============ CAPTION PACK ============ */}
      <section className="max-w-3xl mx-auto px-5 pb-20 space-y-5" data-testid="launch-day-captions">
        <div className="border-l-2 border-[#ff4500] pl-4">
          <h2 className="font-display text-2xl mb-2">Caption pack — tap to copy</h2>
          <p className="text-sm text-[#bbb]">
            Four ready-to-paste captions. Post the first one as your headline reel,
            the others on stories, comments, and personal WhatsApp.
          </p>
        </div>

        {captions.map((c, i) => (
          <article
            key={i}
            className="p-5 border hairline bg-[#1a1a1a]"
            data-testid={`launch-day-caption-${i + 1}`}
          >
            <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500] mb-2">
              {c.title}
            </div>
            <pre className="bg-[#0e0e0e] p-3 mb-3 whitespace-pre-wrap text-sm leading-relaxed font-sans">
              {c.body}
            </pre>
            <button
              type="button"
              onClick={() => {
                try { navigator.clipboard.writeText(c.body); } catch { /* no clipboard */ }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#ff4500] hover:bg-[#e03e00] text-white text-xs font-bold mono uppercase tracking-[0.14em] transition-colors"
              data-testid={`launch-day-copy-${i + 1}`}
            >
              <Sparkles className="w-3.5 h-3.5" /> Copy caption
            </button>
          </article>
        ))}

        <div className="p-5 border-2 border-[#ff4500] bg-[#1a1a1a]" data-testid="launch-day-tips">
          <h3 className="font-display text-xl mb-2 flex items-center gap-2">
            <Tent className="w-5 h-5" strokeWidth={1.7} /> How to use this reel
          </h3>
          <ol className="space-y-2 text-sm text-[#ddd] leading-relaxed list-decimal pl-5">
            <li>Open this page on your phone (or a 540×960 desktop window).</li>
            <li>Screen-record each scene for 2-3 seconds. Five scenes = ~12-15 seconds of footage.</li>
            <li>Stitch in Instagram / TikTok / Reels editor with a sunny acoustic / cinematic sound.</li>
            <li>Paste caption #1 for the post · use caption #2 for stories · #3 for groups · #4 for personal WhatsApp.</li>
            <li>Pin the comment: <strong>camp-compass.uk</strong></li>
            <li>Reply to every comment in the first hour — that's the algorithm trigger.</li>
          </ol>
          <div className="mono text-[10px] text-[#888] mt-4 leading-snug border-t hairline pt-3">
            <Compass className="w-3 h-3 inline-block mr-1" /> Goal: turn launch-day scroll-stoppers into 14-day-trial signups.
            Every signup today = a £1.99/mo Pro subscriber on day 15 unless they cancel.
          </div>
        </div>
      </section>
    </div>
  );
}

function Feature({ icon, label }) {
  return (
    <div className="flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-2.5 text-[11px] sm:text-[13px] font-bold mono uppercase tracking-[0.1em] text-white">
      {icon}
      <span className="truncate">{label}</span>
    </div>
  );
}
